/* Theme-only consent. This is not checkout/server validation or a verified clock.
 * App-owned id/selling_plan inputs are read, never changed. No persisted box is prechecked. */
(() => {
  if (customElements.get('rw-consent')) return;
  const ATTRIBUTE = 'RW Consent';
  const DATA_ATTRIBUTE = 'RW Consent data';
  const VERSION = '1.0';
  const instances = new Set();
  const replay = new WeakSet();
  const revoked = new Set();
  const bundleReplay = new WeakSet();
  let config, cart, configNode, sequence = Promise.resolve(), busy = false, refreshTimer, notice;
  const isCartSurface = instance => ['cart','drawer'].includes(instance?.surface);
  function showNotice(message, instance) {
    if (!notice) {
      notice=document.createElement('dialog');notice.className='rw-consent-notice blocks-radius';
      notice.setAttribute('role','alertdialog');notice.setAttribute('aria-modal','true');
      notice.setAttribute('aria-labelledby','rw-consent-notice-message');
      const copy=document.createElement('p');copy.id='rw-consent-notice-message';
      const close=document.createElement('button');close.type='button';close.className='btn btn--primary';close.dataset.rwNoticeClose='';
      close.addEventListener('click',()=>notice.close());notice.append(copy,close);
      notice.addEventListener('keydown',event=>{if(event.key==='Escape')event.stopPropagation();});
      notice.addEventListener('close',()=>{
        const owner=notice.consentOwner;
        const target=owner?.querySelector('[aria-invalid="true"]') || owner?.querySelector('[data-rw-checkbox]');
        const overlay=owner?.closest('cart-drawer');
        if(target?.isConnected){
          if(overlay?.open)window.FoxTheme?.a11y?.trapFocus(overlay,target);
          else target.focus();
        }
      });
    }
    notice.querySelector('p').textContent=message;
    notice.querySelector('button').textContent=config?.messages.close || 'Close';
    notice.consentOwner=instance;
    if(notice.open)return;
    (instance?.parentElement || document.body).append(notice);
    if(typeof notice.showModal!=='function'){window.alert(message);return;}
    window.FoxTheme?.a11y?.removeTrapFocus();notice.showModal();
  }
  const split = (value) => String(value || '').split(',').map(s => s.trim()).filter(Boolean);
  const strings = (values) => (values || []).map(String);
  const properties = value => Object.entries(Array.isArray(value) ? Object.fromEntries(value) : value || {}).sort(([a],[b])=>a.localeCompare(b));
  const canonicalCart = (value) => JSON.stringify({ currency: value.currency, total: value.total_price,
    items: (value.items || []).map(i => [String(i.id || i.variant_id), Number(i.quantity),
      String(i.plan || i.selling_plan_allocation?.selling_plan?.id || 0), properties(i.properties)]).sort((a,b) => JSON.stringify(a).localeCompare(JSON.stringify(b))) });
  const serialize = (job) => {
    const result = sequence.then(job);
    sequence = result.catch(() => {});
    return result;
  };
  async function digest(text) {
    if (!window.crypto?.subtle) throw new Error('Secure context required for consent fingerprints');
    return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))))
      .map(v => v.toString(16).padStart(2, '0')).join('');
  }
  function parseNode(node) {
    if (!node) throw new Error('Consent configuration unavailable');
    const next = JSON.parse(node.querySelector('[data-rw-config]').textContent);
    // Liquid routes.root_url can be /sv; cart API concatenation requires /sv/.
    next.root = next.root.replace(/\/?$/, '/');
    next.rules.forEach(rule => { rule.html = node.querySelector(`template[data-rw-rule="${CSS.escape(rule.key)}"]`)?.innerHTML || ''; });
    return { config: next, cart: JSON.parse(node.querySelector('[data-rw-cart]').textContent) };
  }
  function readConfig(node = document.querySelector('[data-rw-consent-config]')) {
    const next = parseNode(node);
    config = next.config; cart = next.cart; configNode = node;
  }
  async function getResponse(path, options = {}, format = 'json') {
    const controller=new AbortController();const timer=setTimeout(()=>controller.abort(),15000);
    try {
      const response=await fetch(path,{credentials:'same-origin',...options,signal:controller.signal});
      if(!response.ok)throw new Error(`Shopify ${response.status}`);
      return await response[format]();
    }
    finally {clearTimeout(timer);}
  }
  async function getJSON(path, options = {}) {
    return getResponse(path, options);
  }
  async function fresh() {
    const url = new URL(config.root, location.origin); url.searchParams.set('section_id', 'rw-consent-config');
    const html = await getResponse(url, { cache: 'no-store' }, 'text');
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rw-consent-config]');
    const next = parseNode(node);
    const live = await getJSON(config.root + 'cart.js', { cache: 'no-store' });
    if (canonicalCart(next.cart) !== canonicalCart(live)) throw new Error('Cart changed during refresh');
    config = next.config; cart = next.cart;
    instances.forEach(instance => instance.render());
    return live;
  }
  function productMatches(rule, item) {
    const s = rule.settings, product = item.product || {}, filters = [];
    const chosen = strings(rule.products);
    if (s.product_mode === 'include' && !chosen.includes(String(product.id))) return false;
    if (s.product_mode === 'exclude' && chosen.includes(String(product.id))) return false;
    if (split(s.variants).length) filters.push(split(s.variants).includes(String(item.id)));
    if (split(s.tags).length) filters.push(split(s.tags).some(tag => (product.tags || []).includes(tag)));
    if ((rule.collections || []).length) filters.push(strings(rule.collections).some(id => strings(product.collections).includes(id)));
    if (split(s.product_types).length) filters.push(split(s.product_types).includes(product.type));
    if (s.purchase === 'onetime' && String(item.plan || 0) !== '0') return false;
    if (s.purchase === 'subscription' && String(item.plan || 0) === '0') return false;
    // RTO has no native Shopify flag. Require an explicit variant mapping and a selected plan.
    if (s.purchase === 'rto' && (!split(s.variants).includes(String(item.id)) || String(item.plan || 0) === '0')) return false;
    return !filters.length || (s.combine === 'any' ? filters.some(Boolean) : filters.every(Boolean));
  }
  function matches(rule, context, env = config, cartValue = cart) {
    const s = rule.settings;
    if (!s.active || !rule.customerMatch) return false;
    const group = ['cart','drawer','express_cart'].includes(context.surface) ? 'cart' : 'product';
    if (s.surface !== 'all' && s.surface !== group) return false;
    if (split(s.countries).length && !split(s.countries).map(s=>s.toUpperCase()).includes(env.country)) return false;
    if (split(s.markets).length && !split(s.markets).includes(env.market)) return false;
    if (s.minimum || s.maximum) {
      if (String(s.currency || '').toUpperCase() !== cartValue.currency) return false;
      if (s.minimum && cartValue.total_price < Number(s.minimum) * 100) return false;
      if (s.maximum && cartValue.total_price > Number(s.maximum) * 100) return false;
    }
    return context.items.some(item => productMatches(rule, item));
  }
  function invalidRule(rule) {
    const s = rule.settings;
    return (rule.kind === 'checkbox' && (!/^[A-Za-z0-9_-]{1,64}$/.test(s.internal_id || '') || !s.label?.trim())) ||
      (s.purchase === 'rto' && !split(s.variants).length) ||
      split(s.variants).some(id=>!/^\d+$/.test(id)) ||
      (s.minimum && s.maximum && Number(s.minimum)>Number(s.maximum)) ||
      ((s.minimum || s.maximum) && (!/^[A-Z]{3}$/i.test(s.currency || '') ||
        [s.minimum,s.maximum].some(v => v && (!/^\d+(\.\d{1,2})?$/.test(v) || Number(v) < 0))));
  }
  function ruleVersion(rule) {
    return JSON.stringify([VERSION, config.locale, config.country, config.market, rule]);
  }
  function contextIdentity(context) {
    return JSON.stringify({ surface: ['cart','drawer','express_cart'].includes(context.surface) ? 'cart' : 'product',
      locale: config.locale, country: config.country, market: config.market,
      currency: cart.currency, cart: canonicalCart(cart),
      items: context.items.map(i=>[String(i.id),Number(i.quantity),String(i.plan || 0)]) });
  }
  // Capture the device timezone at acceptance, never infer it from a Shopify Market.
  function acceptanceTime() {
    const now = new Date();
    let timezone = '';
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { /* Offset fallback below. */ }
    return { timestamp: now.toISOString(), timezone, offset: -now.getTimezoneOffset() };
  }
  function plainLabel(html) {
    const template = document.createElement('template');
    template.innerHTML = html || '';
    template.content.querySelectorAll('script,style').forEach(node => node.remove());
    template.content.querySelectorAll('p,div,br,li').forEach(node => node.append(document.createTextNode(' ')));
    return template.content.textContent.replace(/\s+/g, ' ').trim();
  }
  function clockText(timestamp, timezone) {
    try {
      const date = new Date(timestamp);
      if (!Number.isFinite(date.getTime())) return '';
      return new Intl.DateTimeFormat('nb-NO', { timeZone: timezone, day: '2-digit', month: '2-digit',
        year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
        timeZoneName: 'shortOffset' }).format(date);
    } catch (_) { return ''; }
  }
  function customerTime(entry) {
    if (entry.customer_timezone) {
      const formatted = clockText(entry.timestamp_utc, entry.customer_timezone);
      if (formatted) return `${formatted} (${entry.customer_timezone})`;
    }
    // Older records have no device timezone. Do not guess it from country/market.
    const offset = entry.customer_utc_offset_minutes;
    if (Number.isInteger(offset) && Math.abs(offset) <= 840) {
      const shifted = new Date(new Date(entry.timestamp_utc).getTime() + offset * 60000);
      if (Number.isFinite(shifted.getTime())) {
        const pad = value => String(value).padStart(2, '0');
        return `${pad(shifted.getUTCDate())}.${pad(shifted.getUTCMonth()+1)}.${shifted.getUTCFullYear()} kl. ${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}:${pad(shifted.getUTCSeconds())} (UTC${offset < 0 ? '-' : '+'}${pad(Math.floor(Math.abs(offset)/60))}:${pad(Math.abs(offset)%60)}; enhetens tidssoneavvik)`;
      }
    }
    return 'Ikke registrert; se UTC';
  }
  function summary(entries) {
    if (!entries.length) return '';
    const lines = [`Registrerte samtykker: ${entries.length}. Tidspunkt fra kundens enhetsklokke; ikke serververifisert.`];
    for (const [index, entry] of entries.entries()) {
      lines.push('', `${index + 1}. AKSEPTERT — ${entry.label || entry.id}`,
        `ID: ${entry.id} | Tekstversjon: ${entry.version || 'Ikke oppgitt'}`,
        `Norsk tid: ${clockText(entry.timestamp_utc, 'Europe/Oslo') || 'Ikke tilgjengelig; se UTC'} (Europe/Oslo)`,
        `Kundens lokale tid: ${customerTime(entry)}`,
        `UTC: ${entry.timestamp_utc}`,
        `Språk: ${entry.locale} | Butikkland: ${entry.country} | Marked: ${entry.market} | Valuta: ${entry.currency}`,
        `Kjøpsflate: ${entry.scope === 'cart' ? 'Handlekurv' : 'Produkt'} | Handling: ${{pre_checkout:'Før checkout',pre_accelerated_checkout:'Før hurtigbetaling',pre_add_to_cart:'Før legg i handlekurv'}[entry.action] || entry.action}`);
    }
    return lines.join('\n');
  }
  const hasEvidence = live => !!(live.attributes?.[ATTRIBUTE] || live.attributes?.[DATA_ATTRIBUTE]);
  async function persist(live, entries) {
    // Save the readable overview and original technical evidence together, without touching other attributes.
    const value = entries.length ? JSON.stringify({ system: VERSION, entries }) : '';
    const readable = summary(entries);
    if (value.length > 16000 || readable.length > 16000) throw new Error('Consent evidence too large');
    const attributes = { [ATTRIBUTE]: readable, [DATA_ATTRIBUTE]: value };
    const confirmed = response => Object.entries(attributes).every(([key, text]) => (response.attributes?.[key] || '') === text);
    if (confirmed(live)) return live;
    const before = canonicalCart(live);
    const saved = await getJSON(config.root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({attributes}) });
    if (!confirmed(saved) || canonicalCart(saved) !== before) throw new Error('Consent save not confirmed');
    const verified = await getJSON(config.root + 'cart.js', { cache: 'no-store' });
    if (!confirmed(verified) || canonicalCart(verified) !== before) throw new Error('Cart changed after consent save');
    return verified;
  }
  async function retained(live) {
    let entries = [];
    try { entries = JSON.parse(live.attributes?.[DATA_ATTRIBUTE] || live.attributes?.[ATTRIBUTE] || '{}').entries || []; } catch (_) { return []; }
    const keep = [];
    for (const entry of entries) {
      const rule = config.rules.find(r => r.settings.internal_id === entry.id && r.settings.active && r.kind === 'checkbox');
      if (!rule || entry.accepted !== true || entry.source !== 'client_clock_unverified' || entry.locale !== config.locale ||
        entry.country !== config.country || entry.currency !== cart.currency || entry.rule_sha256 !== await digest(ruleVersion(rule))) continue;
      if (revoked.has(entry.id)) continue;
      if (entry.scope === 'cart' && entry.context_sha256 === await digest(canonicalCart(live))) keep.push(entry);
      if (entry.scope === 'product' && entry.items?.length) {
        const items=entry.items.map(([id,quantity,plan])=>cart.items.find(i=>String(i.id)===id && Number(i.quantity)===quantity && String(i.plan||0)===plan));
        if(items.every(Boolean) && matches(rule,{surface:'product',items}))keep.push(entry);
      }
    }
    return keep;
  }
  class RWConsent extends HTMLElement {
    static matches = matches;
    static invalidRule = invalidRule;
    static canonicalCart = canonicalCart;
    connectedCallback() {
      if (!config) { try { readConfig(); } catch (_) { return; } }
      this.accepted = new Map(); this.revision = 0;
      this.fields = this.querySelector('[data-rw-fields]'); this.error = this.querySelector('[data-rw-error]');
      this.product = this.querySelector('[data-rw-product]') ? JSON.parse(this.querySelector('[data-rw-product]').textContent) : null;
      this.products = this.querySelector('[data-rw-products]') ? JSON.parse(this.querySelector('[data-rw-products]').textContent).filter(Boolean) : null;
      this.form = this.closest('products-bundle') || this.closest('form') || (this.dataset.formId ? document.getElementById(this.dataset.formId) : null);
      // Explicit cart/sticky contexts must not inherit a surrounding product surface.
      this.surface = this.dataset.surface;
      if (this.surface === 'product' && (this.closest('quick-view-modal') || this.closest('product-info')?.dataset.viewMode === 'quick-view')) this.surface = 'quick';
      if (['cart','drawer','express_cart'].includes(this.surface)) this.product = null;
      this.owner = this.closest('product-info') || this.closest('sticky-atc-bar') || this.form;
      this.onChange = event => {
        if (event.target.matches('[data-rw-checkbox]')) {
          const key = event.target.dataset.rwCheckbox;
          const id = this.rules.find(r=>r.key===key)?.settings.internal_id;
          if (event.target.checked) {this.accepted.set(key, acceptanceTime());}
          else {
            this.accepted.delete(key);revoked.add(id);
            // Revocation cannot leave another visible copy silently approved.
            instances.forEach(other=>{
              const same=other.rules?.find(r=>r.settings.internal_id===id && r.kind==='checkbox');
              if(other!==this && same){other.accepted.delete(same.key);other.revision++;
                const checkbox=other.querySelector(`[data-rw-checkbox="${CSS.escape(same.key)}"]`);if(checkbox)checkbox.checked=false;}
            });
          }
          this.revision++;
          document.dispatchEvent(new CustomEvent('rw-consent:selection',{detail:{instance:this}}));
          event.target.removeAttribute('aria-invalid'); this.error.hidden = true;
          if (!event.target.checked) reconcile();
        } else this.render();
      };
      this.addEventListener('change', this.onChange);
      this.onOwnerChange = event => { if (!this.contains(event.target)) this.render(); };
      this.owner?.addEventListener('change', this.onOwnerChange);
      this.observer = new MutationObserver(records => {
        if (records.some(r => r.target.matches?.('[name="id"],[name="selling_plan"],[name="quantity"]') ||
          [...r.addedNodes,...r.removedNodes].some(n=>n.matches?.('[name="selling_plan"]')))) this.render();
      });
      if (this.owner) this.observer.observe(this.owner, {subtree:true,childList:true,attributes:true,attributeFilter:['value','disabled']});
      instances.add(this); this.render();
    }
    disconnectedCallback() {
      instances.delete(this); this.observer?.disconnect();
      this.removeEventListener('change',this.onChange);this.owner?.removeEventListener('change',this.onOwnerChange);
      this.revision++;
    }
    context() {
      if (this.products) {
        const items=Array.from(this.form.querySelectorAll('[name="id"]')).map(input=>{
          const product=this.products.find(p=>p.variants.some(v=>String(v.id)===input.value));
          return {id:input.value,quantity:1,plan:'0',product};
        });
        return {surface:this.surface,items,invalid:!items.length || items.some(i=>!i.product)};
      }
      if (!this.product) return {surface:this.surface,items:cart.items};
      const id = this.form?.elements.namedItem('id')?.value;
      const planInputs = Array.from(this.form?.elements || []).filter(i=>i.name==='selling_plan' && !i.disabled);
      const plan = planInputs[0]?.value || '0';
      const quantity = Number(this.form?.elements.namedItem('quantity')?.value || 1);
      const variant = this.product.variants.find(v=>String(v.id)===id);
      return {surface:this.surface,items:variant ? [{id,quantity,plan,product:this.product}] : [],
        invalid: !variant || !Number.isInteger(quantity) || quantity < 1 || planInputs.some(i=>(i.value||'0')!==plan) ||
          (plan !== '0' && !strings(variant?.plans).includes(plan))};
    }
    render() {
      if (!this.fields || !config) return;
      const context = this.context();
      const active = config.enabled && config.surfaces[this.surface === 'card' ? 'product' : this.surface];
      this.rules = active ? config.rules.filter(rule=>matches(rule,context)) : [];
      const requiredConfigError = config.rules.some(r=>r.settings.active && invalidRule(r));
      const ids = config.rules.filter(r=>r.settings.active && r.kind==='checkbox').map(r=>r.settings.internal_id);
      this.badConfig = active && (requiredConfigError || new Set(ids).size !== ids.length);
      const signature = JSON.stringify([this.rules,contextIdentity(context),this.badConfig,active]);
      if (signature === this.signature) return;
      this.closest('rw-consent-payment')?.lock?.();
      this.signature = signature; this.revision++; this.accepted.clear(); this.fields.replaceChildren();this.error.hidden=true;
      this.hidden = !this.rules.length && !this.badConfig;
      if (this.surface === 'card') {this.hidden=true;return;}
      if (this.badConfig) this.showError(config.messages.invalid);
      for (const rule of this.rules) {
        const template = document.createElement('template');template.innerHTML=rule.html;
        const row=document.createElement('div');row.className='rw-consent-row';
        const body=document.createElement('div');body.className='rw-consent-body';body.append(template.content.cloneNode(true));
        if (rule.kind === 'checkbox') {
          const input=document.createElement('input');input.type='checkbox';input.dataset.rwCheckbox=rule.key;
          input.id=`rw-consent-${this.surface}-${rule.key}-${this.dataset.formId || 'drawer'}`;
          input.setAttribute('aria-required',String(!!rule.settings.required));
          const copy=body.querySelector('.rw-consent-copy');const label=document.createElement('label');label.htmlFor=input.id;
          label.innerHTML=copy?.innerHTML || '';
          label.querySelectorAll('p').forEach(p=>{const span=document.createElement('span');span.innerHTML=p.innerHTML;p.replaceWith(span);});
          const marker=document.createElement('span');marker.className='rw-consent-marker text-sm';marker.textContent=rule.settings.required?config.messages.required_label:config.messages.optional;label.append(marker);
          copy?.replaceWith(label);
          const description=body.querySelector('.rw-consent-description');if(description){description.id=input.id+'-description';input.setAttribute('aria-describedby',description.id);}
          row.append(input);
        }
        row.append(body);this.fields.append(row);
      }
      document.dispatchEvent(new CustomEvent('rw-consent:render',{detail:{instance:this}}));
    }
    showError(message) {this.error.textContent=message;this.error.hidden=false;}
    validate(notify = false) {
      this.render();
      const fail=message=>{this.showError(message);if(notify && isCartSurface(this))showNotice(message,this);return false;};
      if (this.surface.startsWith('express_') && this.product && !this.purchaseReady()) {this.showError(config.messages.changed);return false;}
      if (this.badConfig) return fail(config.messages.invalid);
      if (this.context().invalid && config.surfaces[this.surface]) return fail(config.messages.changed);
      const missing=this.rules.filter(r=>r.kind==='checkbox' && r.settings.required && !this.accepted.has(r.key));
      if (!missing.length) return true;
      // A choice can be revoked while automatic wallet preparation is awaiting the cart.
      // Keep payment locked, but only prompt for missing cart choices on a payment attempt.
      if (!notify && isCartSurface(this)) return false;
      this.showError(config.messages.required);
      missing.forEach(r=>this.querySelector(`[data-rw-checkbox="${CSS.escape(r.key)}"]`)?.setAttribute('aria-invalid','true'));
      const first=this.querySelector('[aria-invalid="true"]');first?.focus();this.scrollIntoView({block:'center',behavior:'auto'});
      if(notify && isCartSurface(this))showNotice(config.messages.required,this);
      return false;
    }
    purchaseReady() {
      // Use the existing purchase-options authority, including its pending state.
      const sticky=this.closest('sticky-atc-bar');
      const owner=this.closest('product-info') || (sticky && Array.from(document.querySelectorAll('product-info')).find(i=>i.dataset.viewMode!=='quick-view' && i.dataset.productId===sticky.dataset.productId));
      const options=owner?.querySelector('purchase-options');
      if(options && (!options.isValid() || options.state?.dataset.variantId!==this.form?.elements.namedItem('id')?.value))return false;
      const add=this.form?.querySelector('[name="add"]');
      return !add?.disabled && add?.getAttribute('aria-disabled')!=='true' && !this.context().invalid;
    }
    async evidence() {
      const context=this.context(), scope=this.product||this.products?'product':'cart', entries=[];
      for (const rule of this.rules.filter(r=>r.kind==='checkbox' && this.accepted.has(r.key))) {
        entries.push({id:rule.settings.internal_id,accepted:true,version:rule.settings.version || '',
          label:plainLabel(rule.settings.label),customer_timezone:this.accepted.get(rule.key).timezone || '',
          customer_utc_offset_minutes:this.accepted.get(rule.key).offset,
          rule_sha256:await digest(ruleVersion(rule)),locale:config.locale,country:config.country,market:config.market,currency:cart.currency,
          timestamp_utc:this.accepted.get(rule.key).timestamp,source:'client_clock_unverified',scope,action:this.surface.startsWith('express_')?'pre_accelerated_checkout':scope==='product'?'pre_add_to_cart':'pre_checkout',
          context_sha256:await digest(canonicalCart(cart)),cart_item_count:cart.items.reduce((n,i)=>n+Number(i.quantity),0),
          items:scope==='product'?context.items.map(i=>[String(i.id),Number(i.quantity),String(i.plan||0)]):undefined});
      }
      return entries;
    }
  }
  function surfaceFor(form, checkout) {
    const candidates=Array.from(instances).filter(i=>i.isConnected && i.form===form && !i.surface.startsWith('express_'));
    return candidates.find(i=>checkout?['cart','drawer'].includes(i.surface):!!i.product);
  }
  // Feedback follows the consent request, without disabling or replacing Shopify buttons.
  const loadingOwners = new Set();
  function showLoading(instance, form) {
    const owners = new Set([instance, ...Array.from(instances).filter(owner => owner.form === form)]);
    for (const owner of owners) {
      if (!owner?.isConnected) continue;
      const status = owner.querySelector('[data-rw-loading]');
      if (status) status.hidden = false;
      owner.setAttribute('data-rw-consent-busy', '');
      loadingOwners.add(owner);
    }
    document.querySelectorAll('rw-consent-payment').forEach(gate => {
      if (gate.widget?.form !== form) return;
      gate.setAttribute('data-rw-consent-busy', '');
      gate.opener?.setAttribute('aria-busy', 'true');
      loadingOwners.add(gate);
    });
  }
  function clearLoading() {
    for (const owner of loadingOwners) {
      if (owner.matches('rw-consent')) {
        const status = owner.querySelector('[data-rw-loading]');
        if (status) status.hidden = true;
      }
      owner.removeAttribute('data-rw-consent-busy');
      if (owner.matches('rw-consent-payment')) owner.opener?.removeAttribute('aria-busy');
    }
    loadingOwners.clear();
  }
  async function guard(instance, form, submitter, navigate, notify = true) {
    if (busy) {showLoading(instance, form);if(notify && isCartSurface(instance))showNotice(config.messages.waiting,instance);return;}
    busy=true;form.setAttribute('aria-busy','true');showLoading(instance, form);
    const timer=setTimeout(()=>instance.showError(config.messages.waiting),12000);
    try {
      await serialize(async()=>{
        const live=await fresh();
        if (!instance.isConnected || !instance.validate(notify)) {
          if(config.record && hasEvidence(live)) await persist(live,await retained(live));
          return;
        }
        const revision=instance.revision, signature=instance.signature;
        let committed=[];
        if (config.record) {
          const old=await retained(live), current=await instance.evidence();
          const ids=new Set(instance.rules.filter(r=>r.kind==='checkbox').map(r=>r.settings.internal_id));
          const productScope=!!(instance.product || instance.products);
          const currentItems=JSON.stringify(instance.context().items.map(i=>[String(i.id),Number(i.quantity),String(i.plan||0)]));
          const entries=old.filter(e=>!ids.has(e.id) || (productScope && e.scope==='product' && JSON.stringify(e.items)!==currentItems)).concat(current);
          await persist(live,entries);
          committed=current;
        } else if (hasEvidence(live)) await persist(live,[]);
        instance.render();
        if (!instance.isConnected || revision!==instance.revision || signature!==instance.signature || (instance.surface.startsWith('express_') && instance.product && !instance.purchaseReady())) {
          if(config.record) await persist(await getJSON(config.root+'cart.js'),[]);
          throw new Error('Selection changed while saving');
        }
        committed.forEach(entry=>revoked.delete(entry.id));
        instance.error.hidden=true;
        if (navigate) navigate();
        else {
          replay.add(form);
          try {form.requestSubmit(submitter?.form===form ? submitter : undefined);}
          finally {replay.delete(form);}
        }
      });
    } catch (_) {instance.showError(config.messages.failure);if(notify && isCartSurface(instance))showNotice(config.messages.failure,instance);}
    finally {clearTimeout(timer);clearLoading();busy=false;form.removeAttribute('aria-busy');document.dispatchEvent(new CustomEvent('rw-consent:idle'));}
  }
  // Capture before theme/app submission. Replay the original form so its original
  // validation, selling-plan serialization and AJAX cart handling still run.
  document.addEventListener('submit',event=>{
    if (!config) {
      const widget=event.target.querySelector('rw-consent') || Array.from(document.querySelectorAll('rw-consent')).find(i=>i.dataset.formId===event.target.id);
      if(widget && (event.submitter?.name==='checkout' || event.target.matches('[data-type="add-to-cart-form"]'))) {
        event.preventDefault();event.stopImmediatePropagation();
        const error=widget.querySelector('[data-rw-error]');error.textContent=widget.dataset.rwFailure;error.hidden=false;
      }
      return;
    }
    if (!config.enabled || replay.has(event.target)) return;
    if (event.target.querySelector('rw-consent-payment')?.isReady() && event.submitter?.name !== 'add' && event.submitter?.name !== 'checkout') return;
    const form=event.target,checkout=event.submitter?.name==='checkout';
    const product=form.matches('[data-type="add-to-cart-form"],form[is="product-form"]');
    if(!checkout&&!product)return;
    const instance=surfaceFor(form,checkout);
    if (instance?.surface === 'card' && config.surfaces.product) {
      instance.render();
      if(instance.badConfig || instance.rules.some(r=>r.kind==='checkbox' && r.settings.required)) {
        event.preventDefault();event.stopImmediatePropagation();
        const url=new URL(instance.dataset.productUrl,location.origin);
        url.searchParams.set('variant',form.elements.namedItem('id')?.value || '');location.assign(url);
      }
      return;
    }
    if (!instance || !config.surfaces[instance.surface]) return;
    // A cart choice already validated/saved by its wallet gate needs no second round-trip.
    // Let the real submit event reach Shopify and the existing theme unchanged.
    if(checkout && !busy){
      instance.render();
      const gate=Array.from(document.querySelectorAll('rw-consent-payment')).find(g=>g.cartGate && g.widget===instance);
      if(gate?.isReady())return;
    }
    event.preventDefault();event.stopImmediatePropagation();
    if(instance.validate(true)) guard(instance,form,event.submitter);
  },true);
  document.addEventListener('click',event=>{
    if(!config?.enabled || !config.surfaces.bundle)return;
    const button=event.target.closest?.('products-bundle .products-bundle__button button');
    if(!button || bundleReplay.has(button))return;
    const component=button.closest('products-bundle')?.querySelector('rw-consent');
    if(!component)return;
    event.preventDefault();event.stopImmediatePropagation();
    if(component.validate())guard(component,component.form,null,()=>{
      bundleReplay.add(button);try{button.click();}finally{bundleReplay.delete(button);}
    });
  },true);
  // Theme-owned direct /checkout links are routed through the cart surface.
  // Shopify accelerated buttons (closed shadow DOM) are intentionally NOT intercepted.
  document.addEventListener('click',event=>{
    if(!config?.enabled || (!config.surfaces.cart && !config.surfaces.drawer))return;
    const anchor=event.target.closest?.('a[href]');if(!anchor)return;
    const url=new URL(anchor.href,location.href);
    if(url.origin===location.origin && /\/checkout\/?$/.test(url.pathname)){
      event.preventDefault();event.stopImmediatePropagation();
      const target=Array.from(instances).find(i=>['cart','drawer'].includes(i.surface) && config.surfaces[i.surface]);
      if(target) {
        target.closest('cart-drawer')?.show?.(anchor);
        if(target.validate(true))guard(target,target.form,null,()=>location.assign(url.href));
      } else {
        // No cart surface in this layout: go to the configured cart page for review.
        // Preserve query parameters (for example discount links).
        const destination=new URL(config.cartUrl,location.origin);destination.search=url.search;location.assign(destination);
      }
    }
  },true);
  function reconcile() {
    if(!config?.enabled)return;
    document.querySelectorAll('rw-consent-payment').forEach(g=>g.lock?.());
    instances.forEach(i=>i.revision++);
    clearTimeout(refreshTimer);
    refreshTimer=setTimeout(()=>serialize(async()=>{
      const live=await fresh();const keep=config.record?await retained(live):[];
      // Never turn an unchecked current checkbox back into accepted evidence.
      if(hasEvidence(live))await persist(live,keep.filter(e=>!revoked.has(e.id)));
      document.querySelectorAll('rw-consent-payment').forEach(g=>g.queueReview?.());
    }).catch(()=>{instances.forEach(i=>{if(!i.hidden)i.showError(config.messages.failure);});}),100);
  }
  document.addEventListener('cart:updated',reconcile);
  document.addEventListener('product-ajax:added',reconcile);
  window.addEventListener('pageshow',event=>{if(event.persisted){instances.forEach(i=>{i.signature=null;i.render();});reconcile();}});
  document.addEventListener('shopify:section:load',()=>{
    try{readConfig();instances.forEach(i=>i.render());}catch(_){instances.forEach(i=>i.showError(config?.messages.invalid || 'Consent configuration unavailable'));}
  });
  class RWConsentPayment extends HTMLElement {
    resolveWidget(){
      if(!this.dataset.rwCartSurface)return this.querySelector('rw-consent');
      const form=this.closest('form') || document.getElementById(this.dataset.formId);
      return Array.from(instances).find(i=>i.isConnected && i.form===form && i.surface===this.dataset.rwCartSurface);
    }
    connectedCallback() {
      this.cartGate=!!this.dataset.rwCartSurface;
      this.dialog=this.querySelector('dialog');this.widget=this.resolveWidget();
      this.opener=this.querySelector('[data-rw-open]');this.approve=this.querySelector('[data-rw-approve]');this.wallet=this.querySelector('[data-rw-wallet]');this.status=this.querySelector('[data-rw-payment-status]');
      if(!this.widget?.form){
        // In a newly injected subtree the parent's connectedCallback can precede its children.
        queueMicrotask(()=>{if(this.isConnected && this.resolveWidget()?.form)this.connectedCallback();});return;
      }
      this.controller?.abort();this.observer?.disconnect();clearTimeout(this.reviewTimer);
      this.supportsInert='inert' in HTMLElement.prototype;
      this.toggleAttribute('data-rw-fallback',!this.supportsInert);
      this.controller=new AbortController();const options={signal:this.controller.signal};
      this.opener.addEventListener('click',()=>this.open(),options);
      this.querySelector('[data-rw-close]')?.addEventListener('click',()=>this.dialog.close(),options);
      this.approve?.addEventListener('click',()=>this.confirm(false),options);
      this.dialog?.addEventListener('keydown',event=>{if(event.key==='Escape')event.stopPropagation();},options);
      this.dialog?.addEventListener('close',()=>{
        const ready=this.isReady();
        if(!ready){this.widget.signature=null;this.widget.render();this.widget.revision++;}
        const target=ready?this.wallet:this.opener;
        const overlay=this.closest('quick-view-modal,cart-drawer');
        if(overlay?.open && target.isConnected)window.FoxTheme?.a11y?.trapFocus(overlay,target);
        else if(target.isConnected)target.focus();
      },options);
      document.addEventListener('rw-consent:selection',event=>{
        const source=event.detail.instance;
        if(this.ready && this.revision!==this.widget.revision)this.lock();
        if(source===this.widget){this.lock();if(this.cartGate)this.queueReview();return;}
        if(source.form!==this.widget.form || source.surface.startsWith('express_'))return;
        this.lock();this.copyChoices(source,this.widget);this.queueReview();
      },options);
      document.addEventListener('rw-consent:render',event=>{
        if(event.detail.instance===this.widget){this.lock();this.queueReview();}
      },options);
      document.addEventListener('rw-consent:idle',()=>{if(this.reviewPending)this.queueReview();},options);
      this.observer=new MutationObserver(records=>{
        if(this.ready && this.widget.product && !this.widget.purchaseReady())this.lock();
        else if(!this.ready && this.widget.product && this.widget.purchaseReady() && records.some(r=>r.target.matches?.('[name="id"],[name="selling_plan"],[name="quantity"],[name="add"]')))this.queueReview();
      });
      const owner=this.widget.owner || this.widget.form;
      if(owner)this.observer.observe(owner,{subtree:true,attributes:true,attributeFilter:['disabled','aria-disabled','value']});
      this.lock();this.queueReview();
    }
    disconnectedCallback(){
      this.controller?.abort();this.observer?.disconnect();clearTimeout(this.reviewTimer);this.reviewPending=false;this.ready=false;
      if(this.dialog?.open)this.dialog.close();if(this.widget)this.widget.revision++;
    }
    lock(){
      this.ready=false;
      if(this.cartGate && this.status)this.status.hidden=true;
      if(this.wallet){this.wallet.inert=true;this.wallet.setAttribute('aria-hidden','true');this.wallet.hidden=!this.supportsInert;}
      if(this.opener)this.opener.hidden=false;
    }
    inline(){return this.cartGate?this.widget:Array.from(instances).find(i=>i!==this.widget && i.form===this.widget.form && !i.surface.startsWith('express_') && !i.hidden);}
    copyChoices(source,target){
      if(contextIdentity(source.context())!==contextIdentity(target.context()))return;
      let changed=false;
      for(const rule of target.rules.filter(r=>r.kind==='checkbox')){
        const same=source.rules.find(r=>r.key===rule.key && ruleVersion(r)===ruleVersion(rule));
        if(!same)continue;
        const choice=source.accepted.get(same.key);
        if(target.accepted.get(rule.key)?.timestamp===choice?.timestamp)continue;
        if(choice)target.accepted.set(rule.key,{...choice});else target.accepted.delete(rule.key);
        const checkbox=target.querySelector(`[data-rw-checkbox="${CSS.escape(rule.key)}"]`);if(checkbox){checkbox.checked=!!choice;if(choice)checkbox.removeAttribute('aria-invalid');}
        changed=true;
      }
      if(changed)target.revision++;
    }
    canUseInline(){
      if(this.cartGate)return this.widget.rules.every(rule=>rule.kind!=='checkbox' || !rule.settings.required || this.widget.accepted.has(rule.key));
      const source=this.inline();
      if(!source)return this.widget.rules.length===0 || (this.reviewedSignature===this.widget.signature && this.widget.rules.every(r=>r.kind!=='checkbox' || !r.settings.required || this.widget.accepted.has(r.key)));
      this.copyChoices(source,this.widget);
      return contextIdentity(source.context())===contextIdentity(this.widget.context()) && this.widget.rules.every(rule=>
        source.rules.some(r=>r.key===rule.key && ruleVersion(r)===ruleVersion(rule)) &&
        (rule.kind!=='checkbox' || !rule.settings.required || this.widget.accepted.has(rule.key)));
    }
    queueReview(){
      this.reviewPending=true;clearTimeout(this.reviewTimer);
      this.reviewTimer=setTimeout(()=>{
        if(!this.isConnected || busy)return;
        this.reviewPending=false;this.widget.render();
        if(this.isReady())return;
        if(!this.widget.rules.length && !this.widget.badConfig && (!this.widget.product || this.widget.purchaseReady())){this.unlock();return;}
        if(!this.widget.badConfig && this.canUseInline())this.confirm(true);
      },0);
    }
    isReady(){return !!this.ready && this.widget.isConnected && this.signature===this.widget.signature && this.revision===this.widget.revision && (!this.widget.product || this.widget.purchaseReady());}
    open(){
      if(this.cartGate){
        if(!config || !this.widget?.form)return;
        if(!this.widget.validate(true))return;
        if(busy){this.reviewPending=true;showNotice(config.messages.waiting,this.widget);return;}
        this.confirm(false);return;
      }
      if(typeof this.dialog.showModal!=='function' || !config || !this.widget.form)return;
      this.widget.render();const source=this.inline();if(source)this.copyChoices(source,this.widget);
      window.FoxTheme?.a11y?.removeTrapFocus();this.dialog.showModal();
      // Do not emulate a wallet click after the asynchronous validation.
      if(!this.widget.rules.length && !this.widget.badConfig)this.confirm(false);
    }
    async confirm(automatic){
      if(busy){if(automatic)this.reviewPending=true;return;}
      if(!this.widget.validate(!automatic))return;
      if(this.approve)this.approve.disabled=true;this.status.hidden=true;
      try{await guard(this.widget,this.widget.form,null,()=>{
        // A dismissed dialog must not unlock payments from an old acceptance.
        if(!automatic && !this.cartGate && !this.dialog.open)return;
        const source=this.inline();if(source)this.copyChoices(this.widget,source);
        this.unlock();
      },!automatic);
      // Cart errors already have a visible owner beside the inline checkboxes.
      // Only product dialogs need a status copy outside their closed dialog.
      if(!this.cartGate && !this.ready && this.widget.error && !this.widget.error.hidden){this.status.textContent=this.widget.error.textContent;this.status.hidden=false;}
      }finally{if(this.isConnected && this.approve)this.approve.disabled=false;}
    }
    unlock(){
      this.signature=this.widget.signature;this.revision=this.widget.revision;this.ready=true;
      this.reviewedSignature=this.widget.signature;
      this.wallet.hidden=false;this.wallet.inert=false;this.wallet.removeAttribute('aria-hidden');this.opener.hidden=true;this.status.hidden=true;
      // Keep Shopify's original wallet elements and listeners intact. No synthetic click or re-initialization.
      if(this.dialog?.open)this.dialog.close();
      if(notice?.open && notice.consentOwner===this.widget && notice.querySelector('p').textContent===config.messages.waiting)notice.close();
    }
  }
  try{readConfig();}catch(_){/* No enabled configuration: original storefront remains intact. */}
  customElements.define('rw-consent',RWConsent);
  customElements.define('rw-consent-payment',RWConsentPayment);
  if(config?.hasEvidence)reconcile();
})();
